import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import cn from './cn';

/**
 * shadcn's Dialog, on Radix.
 *
 * This is the one swap that changes behaviour rather than only paint, and all of the
 * changes are in the same direction: Radix traps focus inside the dialog, restores it to
 * the trigger on close, marks the rest of the page `aria-hidden`, and closes on Escape.
 * react-bootstrap's Modal did the first and the last and neither of the middle two.
 *
 * `onOpenChange` fires for every dismissal route, so the wrappers above translate it into
 * the single `onClose` the CMS call sites already pass. `modal` stays on (the default),
 * which is what makes the page behind inert.
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

/**
 * MUI's popups inside a Radix dialog.
 *
 * An MUI Select's menu, an Autocomplete's list and a date or time picker portal to `<body>`,
 * outside the dialog's DOM. While a Radix dialog is open everything outside it is inert:
 * Radix sets `pointer-events: none` on `<body>`, its focus trap pulls focus back from
 * anything outside the dialog, and a press, a wheel or an Escape outside it belongs to the
 * dialog. So the menu drew but took no click (the Role select on Manage Admins), the
 * keyboard could not reach its options, and Escape closed the whole dialog.
 *
 * Three parts put them back, all keyed on `MUI_LAYER`, and on a popup opened over the
 * dialog (one that portalled in after it):
 *   - fields.css gives these layers `pointer-events: auto`;
 *   - `DialogContent` below does not dismiss for a press or an Escape in one (a modal
 *     dialog already ignores focus outside it);
 *   - `releaseMuiLayers` keeps Radix's focus trap and scroll lock off events in one.
 */
export const MUI_LAYER = [
  '.MuiModal-root',
  '.MuiPopover-root',
  '.MuiPopper-root',
  '.MuiAutocomplete-popper',
  '.MuiPickersPopper-root',
].join(', ');

/** Whether a node sits in one of MUI's portalled popups. */
export const isInMuiLayer = (node) =>
  Boolean(node && typeof node.closest === 'function' && node.closest(MUI_LAYER));

/**
 * Marks the content of a CMS dialog: the listener below looks for an open one, and
 * fields.css (`[data-cms-dialog] ~ .MuiPopover-root` and the rest) finds the MUI popups
 * that follow it in `<body>`.
 */
const DIALOG_MARK = 'data-cms-dialog';
const OPEN_DIALOG = `[${DIALOG_MARK}][data-state="open"]`;
const FOLLOWING = 4; // Node.DOCUMENT_POSITION_FOLLOWING, which node has no `Node` for.

/**
 * Whether a node sits in an MUI popup that opened over the open dialog: one that portalled
 * in after it, which is what a Select, an Autocomplete or a picker inside the dialog does.
 * An MUI modal that was already open underneath is left to Radix, as before.
 */
export const isInMuiLayerOverDialog = (node) => {
  const layer = node && typeof node.closest === 'function' ? node.closest(MUI_LAYER) : null;
  if (!layer) return false;
  const dialog = document.querySelector(OPEN_DIALOG);
  return Boolean(dialog && dialog.compareDocumentPosition(layer) & FOLLOWING);
};

/**
 * Radix's focus trap listens for `focusin` and `focusout` on the document, and its scroll
 * lock for `wheel` and `touchmove`, each added when a dialog opens. This listener is added
 * when the module loads, before any dialog can open, so it runs first: for an event in an
 * MUI popup over an open dialog it stops the ones after it. React's own handlers sit on the
 * root and on `<body>`, below the document, so MUI's still run. Exported for the tests.
 */
export const releaseMuiLayers = (event) => {
  // A focusout names where focus is going as its relatedTarget; the rest name their target.
  const node = event.type === 'focusout' ? event.relatedTarget : event.target;
  if (isInMuiLayerOverDialog(node)) event.stopImmediatePropagation();
};

if (typeof document !== 'undefined') {
  ['focusin', 'focusout'].forEach((type) => document.addEventListener(type, releaseMuiLayers));
  ['wheel', 'touchmove'].forEach((type) =>
    document.addEventListener(type, releaseMuiLayers, { passive: true })
  );
}

/** The element an outside-press event from Radix started on. */
const outsideTarget = (event) => event?.detail?.originalEvent?.target ?? event?.target;

/**
 * Wraps one of Radix's dismiss handlers so an event in an MUI popup never closes the dialog
 * (a click on a Select's option, Escape in its menu). The caller's handler still runs.
 */
export const keepOpenForMuiLayers = (handler) => (event) => {
  if (isInMuiLayer(outsideTarget(event))) event.preventDefault();
  if (handler) handler(event);
};

/**
 * Escape closes the innermost thing that is open. With focus in an MUI menu that is the
 * menu; an Autocomplete keeps focus in its input and marks it `aria-expanded` while its list
 * is open, and then it is the list. Otherwise it is the dialog.
 */
export const escapeClosesMuiLayerFirst = (handler) => (event) => {
  const target = event?.target;
  const expandedCombobox =
    target &&
    typeof target.getAttribute === 'function' &&
    target.getAttribute('role') === 'combobox' &&
    target.getAttribute('aria-expanded') === 'true';
  if (expandedCombobox || isInMuiLayer(target)) event.preventDefault();
  if (handler) handler(event);
};

export const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('tw-fixed tw-inset-0 tw-z-dialog tw-bg-ink-50 tw-animate-cms-fade-in', className)}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * `tw-z-dialog` is the layer Bootstrap gave its modals (1050, in tokens.css). Bootstrap is
 * gone; the value stays because the layers around it are placed relative to it: MUI's
 * dialogs at 1300 and the row menu above both.
 */
export const DialogContent = React.forwardRef(
  (
    {
      className,
      children,
      showClose = true,
      closeLabel = 'Close',
      onPointerDownOutside,
      onInteractOutside,
      onEscapeKeyDown,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        {...{ [DIALOG_MARK]: '' }}
        onPointerDownOutside={keepOpenForMuiLayers(onPointerDownOutside)}
        onInteractOutside={keepOpenForMuiLayers(onInteractOutside)}
        onEscapeKeyDown={escapeClosesMuiLayerFirst(onEscapeKeyDown)}
        className={cn(
          // Horizontally centred, vertically anchored near the top. It used to be centred
          // on both axes, which put every dialog in the dead middle of the window; the
          // eye is already in the upper third, where the title and the page actions are.
          // Anchoring the top rather than nudging the centre also means a tall dialog
          // grows downwards and scrolls instead of running off the top of the screen.
          'tw-fixed tw-left-1/2 tw-top-dialog tw-z-dialog',
          'tw--translate-x-1/2',
          'tw-w-full tw-max-w-dialog tw-max-h-dialog tw-overflow-y-auto',
          'tw-bg-white tw-rounded-dialog tw-shadow-xl tw-font-sans tw-text-body tw-text-ink',
          'tw-animate-cms-zoom-in focus:tw-outline-none',
          className
        )}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close
            className={cn(
              'tw-absolute tw-right-3 tw-top-3 tw-bg-transparent tw-border-0 tw-p-1',
              'tw-rounded tw-text-muted tw-leading-none tw-cursor-pointer',
              'hover:tw-text-ink focus-visible:tw-outline-none',
              'focus-visible:tw-ring-2 focus-visible:tw-ring-brand-focus'
            )}
          >
            <X size={18} aria-hidden="true" />
            <span className="tw-sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = 'DialogContent';

export const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn(
      'tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3',
      'tw-border-0 tw-border-b tw-border-solid tw-border-line-light',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

export const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('tw-text-section tw-font-bold tw-text-ink tw-m-0 tw-pr-6', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('tw-text-small tw-text-muted', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export const DialogBody = ({ className, ...props }) => (
  <div className={cn('tw-px-4 tw-py-4', className)} {...props} />
);
DialogBody.displayName = 'DialogBody';

/**
 * The primary action sits on the left, which is not the shadcn default and is deliberate:
 * `main.css:346` floats `.btn-primary` left in every CMS modal footer, so putting it on
 * the right would move the Save button on every screen at once.
 */
export const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      'tw-flex tw-flex-row tw-items-center tw-gap-2 tw-px-4 tw-py-3',
      'tw-border-0 tw-border-t tw-border-solid tw-border-line-light',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';
